import { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const emptyQuestion = () => ({ question: "", options: ["", ""], correct_index: 0, explanation: "" });

export default function QuizzesTab() {
  const { t } = useLang();
  const [quizzes, setQuizzes] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [titleHu, setTitleHu] = useState("");
  const [shared, setShared] = useState(true);
  const [studentIds, setStudentIds] = useState([]);
  const [questions, setQuestions] = useState([emptyQuestion()]);
  const [busy, setBusy] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", title_hu: "", shared: true, student_ids: [] });
  const [toDelete, setToDelete] = useState(null);

  const openEdit = (q) => {
    setEditTarget(q);
    setEditForm({ title: q.title, title_hu: q.title_hu || "", shared: !!q.shared, student_ids: q.student_ids || [] });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/quizzes/${editTarget.id}`, {
        title: editForm.title, title_hu: editForm.title_hu,
        shared: editForm.shared, student_ids: editForm.shared ? [] : editForm.student_ids,
      });
      toast.success(t("admin.quizUpdated"));
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const confirmDelete = async () => {
    const victim = toDelete;
    try {
      await api.delete(`/admin/quizzes/${victim.id}`);
      toast.success(t("admin.quizDeleted"));
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const load = () => {
    api.get("/admin/quizzes").then((r) => setQuizzes(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setQ = (i, patch) => setQuestions(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const setOpt = (qi, oi, val) => setQ(qi, { options: questions[qi].options.map((o, idx) => (idx === oi ? val : o)) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const qs = questions.map((q) => ({ ...q, options: q.options.filter((o) => o.trim()) }));
      await api.post("/admin/quizzes", {
        title, title_hu: titleHu, questions: qs, shared, student_ids: shared ? [] : studentIds,
      });
      toast.success(t("admin.quizCreated"));
      setOpen(false);
      setTitle(""); setTitleHu(""); setShared(true); setStudentIds([]); setQuestions([emptyQuestion()]);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="quizzes-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-serif font-semibold text-lg">{t("admin.quizzes")}</h2>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="create-quiz-btn"><Plus className="w-4 h-4 mr-1" /> {t("admin.newQuiz")}</Button>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        {quizzes.length === 0 && <p className="text-muted-foreground">{t("admin.noData")}</p>}
        {quizzes.map((q) => (
          <Card key={q.id} data-testid={`admin-quiz-${q.id}`}>
            <CardContent className="p-4">
              <p className="font-semibold">{q.title}{q.title_hu ? ` · ${q.title_hu}` : ""}</p>
              <p className="text-xs text-muted-foreground mt-1">{q.questions.length} {t("dash.questions")}</p>
              <div className="mt-3 flex items-center gap-2">
                <Badge variant={q.shared ? "secondary" : "outline"} className="text-xs">
                  {q.shared ? t("admin.sharedAll") : `${t("admin.assignedOnly")} (${q.student_ids.length})`}
                </Badge>
                <Badge variant="outline" className="text-xs">{t("admin.attemptsLabel")}: {q.attempt_count}</Badge>
                {q.avg_score != null && <Badge className="bg-amber-100 text-amber-900 text-xs">{t("admin.avgScore")}: {q.avg_score}%</Badge>}
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(q)} data-testid={`quiz-edit-${q.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> {t("admin.edit")}
                </Button>
                <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10" onClick={() => setToDelete(q)} data-testid={`quiz-delete-${q.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> {t("admin.delete")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="quiz-edit-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{t("admin.editQuiz")}</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">{t("admin.titleLabel")}</Label>
                <Input required value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="quiz-edit-title" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("admin.titleHuLabel")}</Label>
                <Input value={editForm.title_hu} onChange={(e) => setEditForm({ ...editForm, title_hu: e.target.value })} data-testid="quiz-edit-title-hu" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={editForm.shared} onCheckedChange={(v) => setEditForm({ ...editForm, shared: !!v })} data-testid="quiz-edit-shared" />
              {t("admin.sharedAll")}
            </label>
            {!editForm.shared && (
              <div className="max-h-36 overflow-y-auto border border-border rounded-lg p-2 space-y-1.5" data-testid="quiz-edit-students">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={editForm.student_ids.includes(s.id)} onCheckedChange={() => setEditForm({ ...editForm, student_ids: editForm.student_ids.includes(s.id) ? editForm.student_ids.filter((x) => x !== s.id) : [...editForm.student_ids, s.id] })} data-testid={`quiz-edit-student-${s.id}`} />
                    {s.name}
                  </label>
                ))}
              </div>
            )}
            <Button type="submit" className="w-full" data-testid="quiz-edit-save">{t("admin.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="quiz-delete-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{t("admin.deleteQuizTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground" data-testid="quiz-delete-text"><strong>{toDelete?.title}</strong> — {t("admin.deleteQuizConfirm")}</p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setToDelete(null)} data-testid="quiz-delete-cancel">{t("admin.cancel")}</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="quiz-delete-confirm">{t("admin.delete")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quiz-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{t("admin.newQuiz")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">{t("admin.titleLabel")}</Label>
                <Input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="quiz-title-input" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("admin.titleHuLabel")}</Label>
                <Input value={titleHu} onChange={(e) => setTitleHu(e.target.value)} data-testid="quiz-title-hu-input" /></div>
            </div>

            {questions.map((q, qi) => (
              <div key={qi} className="border border-border rounded-lg p-4 space-y-3" data-testid={`quiz-builder-q${qi}`}>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold">{t("admin.question")} {qi + 1}</Label>
                  {questions.length > 1 && (
                    <button type="button" onClick={() => setQuestions(questions.filter((_, idx) => idx !== qi))} className="text-muted-foreground hover:text-destructive" data-testid={`quiz-builder-delete-q${qi}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Input required placeholder={t("admin.question")} value={q.question} onChange={(e) => setQ(qi, { question: e.target.value })} data-testid={`quiz-builder-q${qi}-text`} />
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${qi}`}
                      checked={q.correct_index === oi}
                      onChange={() => setQ(qi, { correct_index: oi })}
                      title={t("admin.correctAnswer")}
                      data-testid={`quiz-builder-q${qi}-correct-${oi}`}
                    />
                    <Input required placeholder={`${t("admin.option")} ${oi + 1}`} value={opt} onChange={(e) => setOpt(qi, oi, e.target.value)} data-testid={`quiz-builder-q${qi}-opt-${oi}`} />
                    {q.options.length > 2 && (
                      <button type="button" onClick={() => setQ(qi, { options: q.options.filter((_, idx) => idx !== oi), correct_index: q.correct_index >= q.options.length - 1 ? 0 : q.correct_index })} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex gap-2">
                  {q.options.length < 6 && (
                    <Button type="button" size="sm" variant="outline" onClick={() => setQ(qi, { options: [...q.options, ""] })} data-testid={`quiz-builder-q${qi}-add-opt`}>
                      + {t("admin.option")}
                    </Button>
                  )}
                </div>
                <Input placeholder={t("admin.explanationLabel")} value={q.explanation} onChange={(e) => setQ(qi, { explanation: e.target.value })} data-testid={`quiz-builder-q${qi}-explanation`} />
              </div>
            ))}

            <Button type="button" variant="outline" onClick={() => setQuestions([...questions, emptyQuestion()])} data-testid="quiz-builder-add-question">
              <Plus className="w-4 h-4 mr-1" /> {t("admin.addQuestion")}
            </Button>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={shared} onCheckedChange={(v) => setShared(!!v)} data-testid="quiz-shared-checkbox" />
              {t("admin.sharedAll")}
            </label>
            {!shared && (
              <div className="max-h-36 overflow-y-auto border border-border rounded-lg p-2 space-y-1.5">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={studentIds.includes(s.id)} onCheckedChange={() => setStudentIds(studentIds.includes(s.id) ? studentIds.filter((x) => x !== s.id) : [...studentIds, s.id])} data-testid={`quiz-student-${s.id}`} />
                    {s.name}
                  </label>
                ))}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={busy} data-testid="quiz-create-submit">{busy ? t("common.loading") : t("admin.newQuiz")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
